import {
  difference,
  find,
  filter,
  forEach,
  intersectionWith,
  isEmpty,
  isEqual,
  isUndefined,
  random,
  round,
  differenceWith
} from 'lodash';
import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { UserAuth } from '../user/user.auth';
import { AnalyticsCommonService } from './analytics.common.service';

const wcmatch = require('wildcard-match');

export interface ITableObject {
  dimension: string;
  title: string;
  column: Array<any>; // eslint-disable-line
  data: Array<any>; // eslint-disable-line
}

enum ViolationMapping {
  'violation10' = 0.1,
  'violation1' = 0.5,
  'violation0' = 0.9
}

export const REFLECTOR_TYPES = [
  {
    key: 'TWAMP',
    value: 'TWAMP'
  },
  {
    key: 'TWAMP-Light',
    value: 'TWAMPLIGHT'
  },
  {
    key: 'Ping',
    value: 'PING'
  },
  {
    key: 'Y.1731',
    value: 'Y1731'
  }
];
export enum TableDataFormat {
  PERCENTAGE = 'percentage',
  MICRO_TO_MILLI = 'micro-to-milli',
  NUMBER = 'number'
}

export enum ViewMode {
  SCORE_VIEW = 'viewByScores',
  KPI_VIEW = 'viewByKpis',
  CHART = 'chart',
  TABLE = 'table'
}

export enum Direction {
  FAR_NEAR = 'Far-Near',
  NEAR_FAR = 'Near-Far',
  BOTH = 'Round-Trip'
}

export enum MapType {
  ROAD_MAP_VIEW = 'Road Map view',
  SATELLITE_VIEW = 'Satellite view'
}

interface FilterExpr {
  key: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class TableDataSourceService {
  public mapFiltersApplied: Subject<any> = new Subject(); // eslint-disable-line
  public refreshClicked: Subject<any> = new Subject(); // eslint-disable-line
  public fuzzyFilterApplied: Subject<any> = new Subject(); // eslint-disable-line
  public flowsUpdated: Subject<any> = new Subject(); // eslint-disable-line
  public endpointsUpdated: Subject<any> = new Subject(); // eslint-disable-line
  public tagsUpdated: Subject<any> = new Subject(); // eslint-disable-line
  public profileUpdated: Subject<any> = new Subject(); // eslint-disable-line
  public exportpdf: Subject<any> = new Subject();
  public flows;
  public freezeZoom;
  public time_range_dimension: string;
  public showSidebarPanel: Subject<any> = new Subject(); // eslint-disable-line
  mapConfigPath = '/en/assets/persistent/analytics-map-config.json';
  mapConfig: any; // eslint-disable-line
  reflectorTypes = REFLECTOR_TYPES;
  public affectedByCriteria = {
    latency: [
      'Fd2wayMaxThreshold',
      'Fd2wayAvgThreshold',
      'Fd1wayMaxNFThreshold',
      'Fd1wayAvgNFThreshold',
      'Fd1wayMaxFNThreshold',
      'Fd1wayAvgFNThreshold'
    ],
    jitter: [
      'Ifdv2wayMaxThreshold',
      'Ifdv2wayAvgThreshold',
      'Ifdv1wayMaxNFThreshold',
      'Ifdv1wayAvgNFThreshold',
      'Ifdv1wayMaxFNThreshold',
      'Ifdv1wayAvgFNThreshold'
    ],
    loss: [
      'Flr2wayAvgThreshold',
      'Flr1wayAvgFNThreshold',
      'Flr1wayAvgNFThreshold',
      'Flr2wayMin',
      'Flr2wayMax',
      'Flr1wayMinNF',
      'Flr1wayMaxNF',
      'Flr1wayMinFN',
      'Flr1wayMaxFN'
    ]
  };
  public tableValueMap = {
    fullDate: 'startTime',
    startTime: 'period_start_time',
    Flow_ID: 'flow_name',
    score: 'overall_score',
    availability: 'availability',
    loss: 'loss_avg',
    latency_min: 'latency_min',
    latency_max: 'latency_max',
    latency_avg: 'latency_avg',
    jitter_min: 'jitter_min',
    jitter_max: 'jitter_max',
    jitter_avg: 'jitter_avg',
    duplicate_count: 'dup_count',
    sequence_count: 'ooo_count',
    timing: 'timing_count'
  };
  public sortValueMap = {
    Availability: 'Availability',
    startTime: 'Start time',
    monitorID: 'Flow',
    score: 'Score',
    Flr2wayAvgThreshold: 'Loss',
    Fd2wayMin: ' Latency Min',
    Fd2wayMaxThreshold: 'Latency Max',
    Fd2wayAvgThreshold: 'Latency Avg',
    Ifdv2wayMin: 'Jitter Min',
    Ifdv2wayMaxThreshold: 'Jitter Max',
    Ifdv2wayAvgThreshold: 'Jitter Avg',
    DupCount: 'Duplicate Count',
    OOOPacketCount: 'Out of Sequence Count',
    TimingCount: 'Timing'
  };
  public granularity: string;
  public dateRangeLabel: string;
  public violationParams = [
    ...this.affectedByCriteria['latency'],
    ...this.affectedByCriteria['jitter'],
    ...this.affectedByCriteria['loss']
  ];
  mapType: MapType;
  private period: number = 5 * 60; // 5 minutes;
  private near_far_svg = `<svg style="transform: scale(.8)">
       <svg xmlns="http://www.w3.org/2000/svg" width="53" height="20">
        <style type="text/css">
          text {
            font-family: Arial,sans-serif;
            font-weight: bold;
            font-size: 10px;
          }
          circle {
            fill: #e5ecf5;
          }
        </style>
        <circle cx="10" cy="10" r="10"/>
        <text x="6.5" y="14"><tspan>N</tspan></text>
        <polygon points="25 13.5, 25 6.5, 30 10"/>
        <circle cx="43" cy="10" r="10"/>
        <text x="40" y="14"><tspan>F</tspan></text>
       </svg>
     </svg>`;

  private far_near_svg = `<svg style="transform: scale(.8)">
       <svg xmlns="http://www.w3.org/2000/svg" width="53" height="20">
        <style type="text/css">
          text {
            font-family: Arial,sans-serif;
            font-weight: bold;
            font-size: 10px;
          }
          circle {
            fill: #e5ecf5;
          }
        </style>
        <circle cx="10" cy="10" r="10"/>
        <text x="7" y="14"><tspan>F</tspan></text>
        <polygon points="25 13.5, 25 6.5, 30 10"/>
        <circle cx="43" cy="10" r="10"/>
        <text x="39.5" y="14"><tspan>N</tspan></text>
       </svg>
     </svg>`;

  constructor(
    private readonly apiService: ApiService,
    private readonly userAuth: UserAuth,
    private analyticsCommonService: AnalyticsCommonService
  ) {
    this.freezeZoom = true;
    this.granularity = 'Minutes';
    // this.getFlows();
  }

  // eslint-disable-next-line
  private static getIconInfo(dataType: any, key?): any {
    return {
      icon: dataType === 'yes' ? 'check_circle' : '',
      label: '',
      key: key
    };
  }

  private static isValid(record) {
    return record['overall_score'] !== null;
  }

  // eslint-disable-next-line
  kpiStatusCategory(element: any, column: any): string {
    if (Array.isArray(element[column])) {
      if (element[column][1] === 10) {
        return 'critical';
      } else if (element[column][1] === 1) {
        return 'warning';
      } else if (element[column][1] === 0) {
        return 'good';
      } else if (element[column].length === 0) {
        return 'good';
      }
      return 'no-threshold';
    }
    return 'good';
  }

  // eslint-disable-next-line
  kpiViolationStatus(direction: any, element: any, column: any): string {
    direction = direction.toLowerCase();
    const category = this.kpiStatusCategory(element, column);
    if (direction === 'rt') {
      const onewayData = element.oneWayData.filteredData;
      const innerColumn = 'inner_' + column;
      const nf_category = this.kpiStatusCategory(onewayData[0], innerColumn);
      const fn_category = this.kpiStatusCategory(onewayData[1], innerColumn);
      return 'rt-' + category + ' rt-nf-' + nf_category + ' rt-fn-' + fn_category;
    } else {
      return direction + '-' + category;
    }
  }

  get timeRangeDimension(): string {
    return this.time_range_dimension;
  }

  set timeRangeDimension(dimension: string) {
    this.time_range_dimension = dimension;
  }

  // Function for fomatting table data in multiple components;
  // eslint-disable-next-line
  formatDataObj(data: any): any {
    const dataObj = JSON.parse(JSON.stringify(data));
    dataObj['Fd2wayMin'] = this.getKpiValue(
      dataObj.latency_min,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd1wayMinNF'] = this.getKpiValue(
      dataObj.latency_min_n_f,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd1wayMinFN'] = this.getKpiValue(
      dataObj.latency_min_f_n,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv1wayMinFN'] = this.getKpiValue(
      dataObj.jitter_min_f_n,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv1wayMinNF'] = this.getKpiValue(
      dataObj.jitter_min_n_f,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd2wayMaxThreshold'] = this.getKpiValue(
      [dataObj.latency_max, dataObj.latency_max_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd2wayAvgThreshold'] = this.getKpiValue(
      [dataObj.latency_avg, dataObj.latency_avg_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv2wayMin'] = this.getKpiValue(
      dataObj.jitter_min,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv2wayMaxThreshold'] = this.getKpiValue(
      [dataObj.jitter_max, dataObj.jitter_max_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv2wayAvgThreshold'] = this.getKpiValue(
      [dataObj.jitter_avg, dataObj.jitter_avg_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd1wayMaxNFThreshold'] = this.getKpiValue(
      [dataObj.latency_max_n_f, dataObj.latency_max_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd1wayAvgNFThreshold'] = this.getKpiValue(
      [dataObj.latency_avg_n_f, dataObj.latency_avg_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv1wayMaxNFThreshold'] = this.getKpiValue(
      [dataObj.jitter_max_n_f, dataObj.jitter_max_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv1wayAvgNFThreshold'] = this.getKpiValue(
      [dataObj.jitter_avg_n_f, dataObj.jitter_avg_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd1wayMaxFNThreshold'] = this.getKpiValue(
      [dataObj.latency_max_f_n, dataObj.latency_max_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Fd1wayAvgFNThreshold'] = this.getKpiValue(
      [dataObj.latency_avg_f_n, dataObj.latency_avg_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv1wayMaxFNThreshold'] = this.getKpiValue(
      [dataObj.jitter_max_f_n, dataObj.jitter_max_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['Ifdv1wayAvgFNThreshold'] = this.getKpiValue(
      [dataObj.jitter_avg_f_n, dataObj.jitter_avg_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.TABLE
    );
    dataObj['DupCountFN'] = this.getKpiValue(
      dataObj.dup_count_f_n,
      TableDataFormat.NUMBER,
      ViewMode.TABLE
    );
    dataObj['DupCountNF'] = this.getKpiValue(
      dataObj.dup_count_n_f,
      TableDataFormat.NUMBER,
      ViewMode.TABLE
    );
    dataObj['DupCount'] = this.getKpiValue(
      dataObj.dup_count,
      TableDataFormat.NUMBER,
      ViewMode.TABLE
    );
    dataObj['OOOPacketCount'] = this.getKpiValue(
      dataObj.ooo_count,
      TableDataFormat.NUMBER,
      ViewMode.TABLE
    );
    dataObj['OOOPacketCountFN'] = this.getKpiValue(
      dataObj.ooo_count_f_n,
      TableDataFormat.NUMBER,
      ViewMode.TABLE
    );
    dataObj['OOOPacketCountNF'] = this.getKpiValue(
      dataObj.ooo_count_n_f,
      TableDataFormat.NUMBER,
      ViewMode.TABLE
    );
    dataObj['Flr2wayAvgThreshold'] = this.getKpiValue(
      [dataObj.loss_avg, dataObj.loss_avg_violation],
      TableDataFormat.PERCENTAGE,
      ViewMode.TABLE
    );
    dataObj['Flr1wayAvgFNThreshold'] = this.getKpiValue(
      [dataObj.loss_avg_f_n, dataObj.loss_avg_f_n_violation],
      TableDataFormat.PERCENTAGE,
      ViewMode.TABLE
    );
    dataObj['Flr1wayAvgNFThreshold'] = this.getKpiValue(
      [dataObj.loss_avg_n_f, dataObj.loss_avg_n_f_violation],
      TableDataFormat.PERCENTAGE,
      ViewMode.TABLE
    );
    dataObj['Availability'] = this.getKpiValue(
      dataObj.availability,
      TableDataFormat.PERCENTAGE,
      ViewMode.TABLE
    );
    dataObj['AvailabilityFN'] = this.getKpiValue(
      dataObj.availability_f_n,
      TableDataFormat.PERCENTAGE,
      ViewMode.TABLE
    );
    dataObj['AvailabilityNF'] = this.getKpiValue(
      dataObj.availability_n_f,
      TableDataFormat.PERCENTAGE,
      ViewMode.TABLE
    );
    dataObj['qm_2way_mos'] = this.roundOff(dataObj['qm_2way_mos'], 2);
    dataObj['qm_1way_mos_n_f'] = this.roundOff(dataObj['qm_1way_mos_n_f'], 2);
    dataObj['qm_1way_mos_f_n'] = this.roundOff(dataObj['qm_1way_mos_f_n'], 2);
    dataObj['qm_2way_r_factor'] = this.roundOff(dataObj['qm_2way_r_factor'], 2);
    dataObj['qm_1way_r_factor_n_f'] = this.roundOff(dataObj['qm_1way_r_factor_n_f'], 2);
    dataObj['qm_1way_r_factor_f_n'] = this.roundOff(dataObj['qm_1way_r_factor_f_n'], 2);
    return dataObj;
  }

  // Function for fomatting chart data in multiple components;
  // eslint-disable-next-line
  formatChartObj(data: any): any {
    const dataObj = JSON.parse(JSON.stringify(data));
    dataObj['Fd2wayMin'] = this.getKpiValue(
      dataObj.latency_min,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd1wayMinNF'] = this.getKpiValue(
      dataObj.latency_min_n_f,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd1wayMinFN'] = this.getKpiValue(
      dataObj.latency_min_f_n,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv1wayMinFN'] = this.getKpiValue(
      dataObj.jitter_min_f_n,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv1wayMinNF'] = this.getKpiValue(
      dataObj.jitter_min_n_f,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd2wayMaxThreshold'] = this.getKpiValue(
      [dataObj.latency_max, dataObj.latency_max_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd2wayAvgThreshold'] = this.getKpiValue(
      [dataObj.latency_avg, dataObj.latency_avg_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv2wayMin'] = this.getKpiValue(
      dataObj.jitter_min,
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv2wayMaxThreshold'] = this.getKpiValue(
      [dataObj.jitter_max, dataObj.jitter_max_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv2wayAvgThreshold'] = this.getKpiValue(
      [dataObj.jitter_avg, dataObj.jitter_avg_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd1wayMaxNFThreshold'] = this.getKpiValue(
      [dataObj.latency_max_n_f, dataObj.latency_max_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd1wayAvgNFThreshold'] = this.getKpiValue(
      [dataObj.latency_avg_n_f, dataObj.latency_avg_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv1wayMaxNFThreshold'] = this.getKpiValue(
      [dataObj.jitter_max_n_f, dataObj.jitter_max_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv1wayAvgNFThreshold'] = this.getKpiValue(
      [dataObj.jitter_avg_n_f, dataObj.jitter_avg_n_f_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd1wayMaxFNThreshold'] = this.getKpiValue(
      [dataObj.latency_max_f_n, dataObj.latency_max_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Fd1wayAvgFNThreshold'] = this.getKpiValue(
      [dataObj.latency_avg_f_n, dataObj.latency_avg_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv1wayMaxFNThreshold'] = this.getKpiValue(
      [dataObj.jitter_max_f_n, dataObj.jitter_max_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['Ifdv1wayAvgFNThreshold'] = this.getKpiValue(
      [dataObj.jitter_avg_f_n, dataObj.jitter_avg_f_n_violation],
      TableDataFormat.MICRO_TO_MILLI,
      ViewMode.CHART
    );
    dataObj['DupCountFN'] = this.getKpiValue(
      dataObj.dup_count_f_n,
      TableDataFormat.NUMBER,
      ViewMode.CHART
    );
    dataObj['DupCountNF'] = this.getKpiValue(
      dataObj.dup_count_n_f,
      TableDataFormat.NUMBER,
      ViewMode.CHART
    );
    dataObj['DupCount'] = this.getKpiValue(
      dataObj.dup_count,
      TableDataFormat.NUMBER,
      ViewMode.CHART
    );
    dataObj['OOOPacketCount'] = this.getKpiValue(
      dataObj.ooo_count,
      TableDataFormat.NUMBER,
      ViewMode.CHART
    );
    dataObj['OOOPacketCountFN'] = this.getKpiValue(
      dataObj.ooo_count_f_n,
      TableDataFormat.NUMBER,
      ViewMode.CHART
    );
    dataObj['OOOPacketCountNF'] = this.getKpiValue(
      dataObj.ooo_count_n_f,
      TableDataFormat.NUMBER,
      ViewMode.CHART
    );

    dataObj['Flr2wayMin'] = this.getKpiValue(
      dataObj.loss_min,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['Flr1wayMinNF'] = this.getKpiValue(
      dataObj.loss_min_n_f,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['Flr1wayMinFN'] = this.getKpiValue(
      dataObj.loss_min_f_n,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );

    dataObj['Flr2wayMax'] = this.getKpiValue(
      dataObj.loss_max,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['Flr1wayMaxNF'] = this.getKpiValue(
      dataObj.loss_max_n_f,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['Flr1wayMaxFN'] = this.getKpiValue(
      dataObj.loss_max_f_n,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );

    dataObj['Flr2wayAvgThreshold'] = this.getKpiValue(
      [dataObj.loss_avg, dataObj.loss_avg_violation],
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['Flr1wayAvgFNThreshold'] = this.getKpiValue(
      [dataObj.loss_avg_f_n, dataObj.loss_avg_f_n_violation],
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['Flr1wayAvgNFThreshold'] = this.getKpiValue(
      [dataObj.loss_avg_n_f, dataObj.loss_avg_n_f_violation],
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );

    dataObj['Availability'] = this.getKpiValue(
      dataObj.availability,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['AvailabilityFN'] = this.getKpiValue(
      dataObj.availability_f_n,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['AvailabilityNF'] = this.getKpiValue(
      dataObj.availability_n_f,
      TableDataFormat.PERCENTAGE,
      ViewMode.CHART
    );
    dataObj['TwampLatency'] =
      ViolationMapping[
        'violation' +
          Math.max(
            dataObj.latency_avg_f_n_violation,
            dataObj.latency_avg_n_f_violation,
            dataObj.latency_avg_violation,
            dataObj.latency_max_f_n_violation,
            dataObj.latency_max_n_f_violation,
            dataObj.latency_max_violation
          )
      ];
    dataObj['TwampLoss'] =
      ViolationMapping[
        'violation' +
          Math.max(
            dataObj.loss_avg_f_n_violation,
            dataObj.loss_avg_n_f_violation,
            dataObj.loss_avg_violation
          )
      ];
    dataObj['TwampJitter'] =
      ViolationMapping[
        'violation' +
          Math.max(
            dataObj.jitter_avg_f_n_violation,
            dataObj.jitter_avg_n_f_violation,
            dataObj.jitter_avg_violation,
            dataObj.jitter_max_f_n_violation,
            dataObj.jitter_max_n_f_violation,
            dataObj.jitter_max_violation
          )
      ];
    dataObj['qm_2way_mos'] = this.roundOff(dataObj['qm_2way_mos'], 2);
    dataObj['qm_1way_mos_n_f'] = this.roundOff(dataObj['qm_1way_mos_n_f'], 2);
    dataObj['qm_1way_mos_f_n'] = this.roundOff(dataObj['qm_1way_mos_f_n'], 2);
    dataObj['qm_2way_r_factor'] = this.roundOff(dataObj['qm_2way_r_factor'], 2);
    dataObj['qm_1way_r_factor_n_f'] = this.roundOff(dataObj['qm_1way_r_factor_n_f'], 2);
    dataObj['qm_1way_r_factor_f_n'] = this.roundOff(dataObj['qm_1way_r_factor_f_n'], 2);
    return dataObj;
  }

  // for extracting and formatting the kpi value
  // eslint-disable-next-line
  getKpiValue(kpi: any, conversionFormat: any, viewFor?: any): any {
    // percentage --> roundoff to 2
    function roundOff(value: number, decimals: number) {
      const shift = Math.pow(10, decimals);
      return Math.round(value * shift + 0.00000000000001) / shift;
    }

    // micro to milli --> divide by 1000 and roundoff to 3
    if (isUndefined(kpi)) {
      if (viewFor == 'table') {
        return [];
      } else if (viewFor == 'chart') {
        return 0;
      }
    } else {
      if (Array.isArray(kpi) && !kpi.length) {
        return kpi;
      }
      let val = Array.isArray(kpi)
        ? kpi[0] === null
          ? kpi[0]
          : Number(kpi[0])
        : kpi === null
        ? kpi
        : Number(kpi);
      let roundOffValue: number;
      switch (conversionFormat) {
        case 'percentage':
          roundOffValue = 2;
          break;
        case 'micro-to-milli':
          roundOffValue = 3;
          val = val === null ? val : val / 1000;
          break;
        case 'number':
          return val;
      }
      if (Array.isArray(kpi)) {
        return [val === null ? val : roundOff(val, roundOffValue), kpi[1]];
      } else {
        return val === null ? val : roundOff(val, roundOffValue);
      }
    }
  }

  // Now this logic is changed to apply status based on a particular violation selected
  // eslint-disable-next-line
  applyViolationBasedStatus(data: any, filters: any): any {
    return forEach(data, item => {
      const flowData = item['properties'] ? item['properties'] : item;
      if (filters.indexOf('latency') !== -1) {
        this.setFlowStatus(flowData, this.affectedByCriteria['latency'], true);
      } else if (filters.indexOf('jitter') !== -1) {
        this.setFlowStatus(flowData, this.affectedByCriteria['jitter'], true);
      } else if (filters.indexOf('loss') !== -1) {
        this.setFlowStatus(flowData, this.affectedByCriteria['loss'], true);
      } else {
        this.setStatus(flowData, ViewMode.KPI_VIEW);
      }
    });
  }

  // eslint-disable-next-line
  setFlowStatus(item: any, violationParams: any, showOnly?: any): any {
    if (violationParams.some(e => item[e] && Array.isArray(item[e]) && item[e][1] == 10)) {
      item.status = 'invalid';
    } else if (violationParams.some(e => item[e] && Array.isArray(item[e]) && item[e][1] == 1)) {
      item.status = 'warning';
    } else if (violationParams.some(e => item[e] && Array.isArray(item[e]) && item[e][1] == 0)) {
      item.status = 'valid';
    } else if (violationParams.some(e => item[e] && Array.isArray(item[e]) && item[e][1] == -1)) {
      item.status = 'valid';
    } else if (showOnly) {
      item.status = '';
    } else {
      item.status = 'valid';
    }
  }

  // eslint-disable-next-line
  setStatus(item: any, viewMode: any): any {
    if (viewMode == ViewMode.KPI_VIEW) {
      // if the flow has any kpi violation with value 10 --> invalid
      // if it not invalid, and if any kpi has violation value 1 --> warning
      // if not invalid or warning, i.e., no violations --> valid
      const filters = this.analyticsCommonService.getFilters();
      let params = this.violationParams;
      if (filters && filters.affectedByFiltersArray.length > 0) {
        params = this.affectedByCriteria[filters.affectedByFiltersArray[0]];
      }

      this.setFlowStatus(item, params, false);
    } else if (viewMode == ViewMode.SCORE_VIEW) {
      if (item.score === 10) {
        item.status = 'valid';
      } else if (item.score >= 0 && item.score <= 2) {
        item.status = 'invalid';
      } else {
        item.status = 'warning';
      }
    }
    return item.status;
  }

  setFlowMutedFlag(flowId: string): void {
    const muted = find(this.flows, item => {
      return item.flow_name === flowId;
    });
    if (muted) {
      muted.ui_monitoring_status = 'mute';
    }
  }

  // eslint-disable-next-line
  filterByScoreRange(data: any, scoreRange): any {
    return filter(data, item => {
      const flowData = item['properties'] ? item['properties'] : item;
      return flowData['score'] >= scoreRange.min && flowData['score'] <= scoreRange.max;
    });
  }

  // eslint-disable-next-line
  getFlows$(page: number): Observable<any> {
    const url = '/flowdb/api/flows?size=1000' + '&page=' + page + '&sort=id,asc';
    return this.apiService.get$<any>(url, {}); // eslint-disable-line
  }

  // eslint-disable-next-line
  getMapConfig$(): Observable<any> {
    const configurl = this.mapConfigPath;
    // eslint-disable-next-line
    let response: Observable<any>;
    try {
      response = this.apiService.get$<any>(configurl, {}); // eslint-disable-line
    } catch {
      // eslint-disable-next-line
      console.info('No custom map configuration was available from the server.');
    }
    return response;
  }

  getFlows(): void {
    this.getFlows$(0).subscribe(response => {
      let flows = response ? response._embedded.flows : null;
      const totalElements = response.page.totalElements;
      const pageSize = response.page.totalPages;
      if (pageSize === 1 && flows.length > 0) {
        this.flows = flows;
      }
      for (let i = 1, page = 0; i < pageSize; i++) {
        page = page + 1;
        this.getFlows$(page).subscribe(anotherPage => {
          flows = flows.concat(anotherPage._embedded.flows);
          if (flows.length === totalElements) {
            this.flows = flows;
          }
        });
      }
    });
  }

  // eslint-disable-next-line
  getTableSourceData(type: any, resData: any, params?: any, tokens?: any, isUtc?: boolean): any {
    const template = this.getTableTemplate(this.getType(type), params);
    const temp = [];
    forEach(resData, item => {
      temp.push(this.getTableObject(type, item, isUtc));
    });
    template.data = temp;
    return template;
  }

  // eslint-disable-next-line
  getFilteredFlowNames(flows: any, tokens: any): any {
    const allFilter = [];
    const groupedFilter = [];
    const filteredFlowNames = [];
    const falsyFilter = [];
    const exp = this.getExpressionFromTokens(tokens);
    const operators = this.getOperatorsFromTokens(tokens);
    // convert expression string to filter array
    forEach(exp, item => {
      item = item.replace(/~/g, '=');
      if (item.includes('!=')) {
        const key = item.split(' != ')[0];
        let val = item.split(' != ')[1];
        val = val.substring(1, val.length - 1);
        falsyFilter.push({ key: key, value: val });
        allFilter.push({ key: key, value: val });
      } else {
        const key = item.split(' = ')[0];
        let val = item.split(' = ')[1];
        val = val.substring(1, val.length - 1);
        allFilter.push({ key: key, value: val });
      }
    });

    // if there is an operator then there are more than one expression
    // grouping the filter expr for different operators
    // the grouping method followed here is, statements with AND in between are grouped together,
    // OR statements are considered as individual filter
    // Ex., (Province = Sofia AND Postcode = 1407) OR (Postcode = 1151)
    if (!isEmpty(operators)) {
      groupedFilter.push([allFilter.shift()]);
      for (const opr of operators) {
        if (opr == 'AND') {
          groupedFilter.push([...groupedFilter.pop(), allFilter.shift()]);
        } else if (opr == 'OR') {
          groupedFilter.push([allFilter.shift()]);
        }
      }
    }

    // compare the filter expression with each flow
    forEach(flows, flowData => {
      const flowFilters = [];
      const flowTags = [];
      flowFilters.push({ key: 'Flow', value: flowData.flow_name });
      if (flowData?.reflector_type) {
        flowFilters.push({ key: 'ReflectorType', value: flowData.reflector_type });
      }
      if (flowData.sla_profile) {
        flowFilters.push({ key: 'Profile', value: flowData.sla_profile });
      }
      if (flowData.tags) {
        flowFilters.push(...this.formTags(flowData.tags));
      }
      if (flowData.endpoints[0]) {
        flowFilters.push({
          key: 'Endpoint',
          value: flowData.endpoints[0].endpoint_name
        });
        if (flowData.endpoints[0].tags) {
          flowFilters.push(...this.formTags(flowData.endpoints[0].tags));
        }
      }
      if (flowData.endpoints[1]) {
        flowFilters.push({
          key: 'Endpoint',
          value: flowData.endpoints[1].endpoint_name
        });
        if (flowData.endpoints[1].tags) {
          flowFilters.push(...this.formTags(flowData.endpoints[1].tags));
        }
      }
      flowFilters.forEach(tag => {
        flowTags.push({ key: tag.key, value: '' });
      });

      if (!isEmpty(groupedFilter)) {
        const anyConditionsPassed = [];
        forEach(groupedFilter, filter => {
          if (filter.length == 1) {
            anyConditionsPassed.push(
              this.conditionToCheckOR(flowFilters, filter, falsyFilter, flowTags)
            );
          } else if (filter.length > 1) {
            anyConditionsPassed.push(
              this.conditionToCheckAND(flowFilters, filter, falsyFilter, flowTags)
            );
          }
        });

        if (anyConditionsPassed.includes(true)) {
          filteredFlowNames.push(flowData.flow_name);
        }
      } else {
        if (!isEmpty(falsyFilter)) {
          falsyFilter.forEach(exp => {
            exp.value = exp.value.trim();
          });
          if (falsyFilter[0].value.includes('*')) {
            const match = wcmatch(this.createWildcardmatchString(falsyFilter[0].value), {
              flags: 'i'
            });
            const data = flowFilters
              .filter(item => item.key.trim() === falsyFilter[0].key.trim())
              .map(val => val.value);
            if (data.filter(match).length === 0) {
              filteredFlowNames.push(flowData.flow_name);
            }
          } else if (falsyFilter[0].value.length === 0) {
            let found = false;
            flowTags.forEach(item => {
              if (item.key === falsyFilter[0].key) {
                found = true;
              }
              if (!found) {
                filteredFlowNames.push(flowData.flow_name);
              }
            });
          } else if (!flowFilters.some(tag => find(falsyFilter, tag))) {
            filteredFlowNames.push(flowData.flow_name);
          }
        } else {
          allFilter.forEach(exp => {
            exp.value = exp.value.trim();
          });
          if (allFilter[0].value.includes('*')) {
            const match = wcmatch(this.createWildcardmatchString(allFilter[0].value), {
              flags: 'i'
            });
            const data = flowFilters
              .filter(item => item.key.trim() === allFilter[0].key.trim())
              .map(val => val.value);
            if (data.some(match)) {
              filteredFlowNames.push(flowData.flow_name);
            }
          } else if (allFilter[0].value.length === 0) {
            let found = false;
            flowTags.forEach(item => {
              if (item.key === allFilter[0].key) {
                found = true;
              }
              if (found) {
                filteredFlowNames.push(flowData.flow_name);
              }
            });
          } else if (flowFilters.some(tag => find(allFilter, tag))) {
            filteredFlowNames.push(flowData.flow_name);
          }
        }
      }
    });
    return filteredFlowNames;
  }

  // eslint-disable-next-line
  conditionToCheckAND(flowFilters: any, filters: any, falsyFilter: any, flowTags: any): any {
    let fval = true,
      fwval = true,
      sval: boolean,
      wval: boolean;
    falsyFilter.forEach(exp => {
      exp.value = exp.value.trim();
    });
    filters.forEach(exp => {
      exp.value = exp.value.trim();
    });
    // pick the falsy expression for this filter
    const falsyExpr = intersectionWith(filters, falsyFilter, isEqual);
    if (falsyExpr.length) {
      const wildcardMatchFalseFilters = falsyExpr.filter(item => item.value.includes('*'));
      const stringMatchFalseFilters = falsyExpr.filter(
        item => item.value && !item.value.includes('*')
      );
      const onlyTagMatchingFalseFilters = falsyExpr.filter(item => !item.value);
      if (stringMatchFalseFilters.length > 0 && onlyTagMatchingFalseFilters.length > 0) {
        fval =
          !stringMatchFalseFilters.some(filter => find(flowFilters, filter)) &&
          !onlyTagMatchingFalseFilters.some(filter => find(flowTags, filter));
      } else if (stringMatchFalseFilters.length > 0) {
        fval = !stringMatchFalseFilters.some(filter => find(flowFilters, filter));
      } else if (onlyTagMatchingFalseFilters.length > 0) {
        fval = !onlyTagMatchingFalseFilters.some(filter => find(flowTags, filter));
      } else {
        fval = true;
      }
      if (difference(filters.filter(item => !item.value.includes('*'))).length > 0) {
        filters.forEach(exp => {
          exp.value = exp.value.trim();
        });
        const onlyTagMatchingFilters = this.getArrayDifference(
          filters.filter(item => !item.value),
          falsyFilter,
          ['key', 'value']
        );
        const filterWithValue = this.getArrayDifference(
          filters,
          filters.filter(item => !item.value),
          ['key', 'value']
        );
        if (
          onlyTagMatchingFilters.length > 0 &&
          difference(
            filterWithValue.filter(item => item.value && !item.value.includes('*')),
            stringMatchFalseFilters
          ).length > 0
        ) {
          sval =
            difference(
              filterWithValue.filter(item => item.value && !item.value.includes('*')),
              stringMatchFalseFilters
            ).every(filter => find(flowFilters, filter)) ||
            onlyTagMatchingFilters.every(filter => find(flowTags, filter));
        } else if (
          difference(
            filterWithValue.filter(item => item.value && !item.value.includes('*')),
            stringMatchFalseFilters
          ).length > 0
        ) {
          sval = differenceWith(
            filterWithValue.filter(item => item.value && !item.value.includes('*')),
            stringMatchFalseFilters,
            (a, b) => {
              return a.key === b.key && a.value === b.value;
            }
          ).every(filter => find(flowFilters, filter));
        } else if (onlyTagMatchingFilters.length > 0) {
          sval = onlyTagMatchingFilters.every(filter => find(flowTags, filter));
        }
      } else {
        sval = true;
      }
      if (wildcardMatchFalseFilters.length > 0) {
        fwval = !wildcardMatchFalseFilters.some(item =>
          flowFilters.find(el => {
            const match = wcmatch(this.createWildcardmatchString(item.value), {
              flags: 'i'
            });
            const value = el.value;
            return match(value);
          })
        );
      } else {
        fwval = true;
      }
      if (
        difference(
          filters.filter(item => item.value.includes('*')),
          wildcardMatchFalseFilters
        ).length > 0
      ) {
        wval = difference(
          filters.filter(item => item.value.includes('*')),
          wildcardMatchFalseFilters
        ).every(item =>
          flowFilters.find(el => {
            const match = wcmatch(this.createWildcardmatchString(item.value), {
              flags: 'i'
            });
            const value = el.value;
            return match(value);
          })
        );
      } else {
        wval = true;
      }
      return sval && fval && fwval && wval;
    }
    filters.forEach(exp => {
      exp.value = exp.value.trim();
    });
    const wildcardMatchFilters = filters.filter(item => item.value.includes('*'));
    const stringMatchFilters = filters.filter(item => item.value && !item.value.includes('*'));
    const onlyTagMatchingFilters = filters.filter(item => !item.value);
    if (stringMatchFilters.length > 0 || onlyTagMatchingFilters.length > 0) {
      let stringVal = true;
      let tagVal = true;
      if (stringMatchFilters.length > 0) {
        stringVal = stringMatchFilters.every(filter => find(flowFilters, filter));
      }
      if (onlyTagMatchingFilters.length > 0) {
        tagVal = onlyTagMatchingFilters.every(filter => find(flowTags, filter));
      }
      sval = stringVal && tagVal;
    } else {
      sval = true;
    }
    if (wildcardMatchFilters.length > 0) {
      wval = wildcardMatchFilters.every(item =>
        flowFilters.find(el => {
          const match = wcmatch(this.createWildcardmatchString(item.value), { flags: 'i' });
          const value = el.value;
          return match(value);
        })
      );
    } else {
      wval = true;
    }
    return sval && wval;
  }

  // eslint-disable-next-line
  conditionToCheckOR(flowFilters: any, filter: any, falsyFilter: any, flowTags: any): any {
    falsyFilter.forEach(exp => {
      exp.value = exp.value.trim();
    });
    filter.forEach(exp => {
      exp.value = exp.value.trim();
    });
    let fval = false,
      val = true;
    const falsyExpr = intersectionWith(filter, falsyFilter, isEqual);
    if (falsyExpr.length) {
      if (falsyExpr[0].value && falsyExpr[0].value.includes('*')) {
        const match = wcmatch(this.createWildcardmatchString(falsyExpr[0].value), {
          flags: 'i'
        });
        const data = flowFilters
          .filter(item => item.key.trim() === falsyExpr[0].key.trim())
          .map(val => val.value);
        fval = data.filter(match).length === 0;
      } else if (!falsyExpr[0].value) {
        fval = !flowTags.some(tag => find(filter, tag));
      } else {
        fval = !flowFilters.some(tag => find(falsyExpr, tag));
      }
      return fval; // return directly as anyway OR's are single statements
    }
    if (filter[0].value && filter[0].value.includes('*')) {
      const match = wcmatch(this.createWildcardmatchString(filter[0].value), {
        flags: 'i'
      });
      const data = flowFilters
        .filter(item => item.key.trim() === filter[0].key.trim())
        .map(val => val.value);
      val = data.some(match);
    } else if (!filter[0].value) {
      val = flowTags.some(tag => find(filter, tag));
    } else {
      val = flowFilters.some(tag => find(filter, tag));
    }
    return val;
  }

  // eslint-disable-next-line
  formTags(tagData: any): any {
    const flowDetailsTags = [];
    forEach(tagData, item => {
      flowDetailsTags.push({ key: item.tag_name, value: item.tag_value });
    });
    return flowDetailsTags;
  }

  // eslint-disable-next-line
  getKPI_Violation(): any {
    const violation = random(5, 10);
    const violationStatus = violation > 7 ? 'critical' : 'warning';
    return { value: violation, class: violationStatus };
  }

  // eslint-disable-next-line
  getScoreStatus(score: any): string {
    return 'score-' + score;
  }

  // eslint-disable-next-line
  getAvailability(valueOfUAS: any): any {
    let availableTime = this.period / valueOfUAS;
    availableTime = 100 - 100 / availableTime;
    return { value: round(availableTime, 2), unit: '%' };
  }

  // eslint-disable-next-line
  getFlowsAvailability(lossPercent: any): any {
    const availability = 100 - parseFloat(lossPercent);
    return { value: round(availability, 2), unit: '%' };
  }

  // TODO - remove - should be unused
  // eslint-disable-next-line
  getUAS(entity: any): any {
    const FLR = (entity.FLR_1way_Avg_F_N + entity.FLR_1way_Avg_N_F) / 2;
    const calcUAS = this.period * (FLR / 100);
    return round(calcUAS / 60, 3);
  }

  getTimingValue(timingCount: number): string {
    switch (timingCount) {
      case 0:
        return 'Synced';
      case 1:
        return 'Not Synced';
      case 2:
        return 'Synthetic';
      default:
        return '';
    }
  }

  // eslint-disable-next-line
  getTableTemplate(type: any, params?: any): ITableObject {
    let colName = null;
    if (params && params.length > 0) {
      colName = params[0].columnName;
    }
    switch (type) {
      case 'flows_table_view': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'score',
              title: 'Score',
              type: 'badge-score'
            },
            {
              id: 'Flow_ID',
              title: 'Flow',
              type: 'text-link'
            },
            {
              id: 'availability',
              title: 'Availability (%)',
              type: 'text'
            },
            {
              id: 'loss',
              title: 'Loss (%)',
              type: 'text',
              class: colName === 'loss' ? 'highlightCol' : ''
            },
            {
              id: 'latency_min',
              title: 'Latency',
              type: 'text',
              sub_text: 'Min (ms)'
            },
            {
              id: 'latency_max',
              title: 'Latency',
              type: 'text',
              sub_text: 'Max (ms)'
            },
            {
              id: 'latency_avg',
              title: 'Latency',
              type: 'text',
              sub_text: 'Avg (ms)',
              class: colName === 'latency_avg' ? 'highlightCol' : ''
            },
            {
              id: 'jitter_min',
              title: 'Jitter',
              type: 'text',
              sub_text: 'Min (ms)'
            },
            {
              id: 'jitter_max',
              title: 'Jitter',
              type: 'text',
              sub_text: 'Max (ms)'
            },
            {
              id: 'jitter_avg',
              title: 'Jitter',
              type: 'text',
              sub_text: 'Avg (ms)',
              class: colName === 'jitter_avg' ? 'highlightCol' : ''
            },
            {
              id: 'sequence_count',
              title: 'Out of Sequence',
              type: 'text',
              sub_text: 'Count'
            },
            {
              id: 'duplicate_count',
              title: 'Duplicate',
              type: 'text',
              sub_text: 'Count'
            },
            {
              id: 'timing',
              title: 'Timing',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'alarms_table_view': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'periodStartTime',
              title: 'Period Start Time',
              type: 'date-time'
            },
            {
              id: 'flow',
              title: 'Flow Name',
              type: 'text'
            },
            {
              id: 'profile',
              title: 'Profile Name',
              type: 'text'
            },
            {
              id: 'severity',
              title: 'Severity',
              type: 'text'
            },
            {
              id: 'perspective',
              title: 'Perspective',
              type: 'text'
            },
            {
              id: 'KPIName',
              title: 'KPI Name',
              type: 'text'
            },
            {
              id: 'KPIValue',
              title: 'KPI Value',
              type: 'kpi-text'
            },
            {
              id: 'ThresholdValue',
              title: 'Threshold Value',
              type: 'text'
            },
            {
              id: 'alarm_muted',
              title: 'Alarm Muted',
              type: 'mat-icon'
            },
            {
              id: 'alarmCreated',
              title: 'Alarm Created',
              type: 'date-time-second'
            },
            {
              id: 'actionMenu',
              title: 'Actions',
              type: 'action-icon',
              disableSorting: true
            }
          ],
          data: []
        };
      }
      case 'flow_detail_table_view': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'score',
              title: 'Score',
              type: 'badge-score'
            },
            {
              id: 'startTime',
              title: 'Start Time',
              type: 'text'
            },
            {
              id: 'availability',
              title: 'Availability (%)',
              type: 'text'
            },
            {
              id: 'loss',
              title: 'Loss (%)',
              type: 'text',
              class: colName === 'loss' ? 'highlightCol' : ''
            },
            {
              id: 'latency_min',
              title: 'Latency',
              type: 'text',
              sub_text: 'Min (ms)'
            },
            {
              id: 'latency_max',
              title: 'Latency',
              type: 'text',
              sub_text: 'Max (ms)'
            },
            {
              id: 'latency_avg',
              title: 'Latency',
              type: 'text',
              sub_text: 'Avg (ms)',
              class: colName === 'latency_avg' ? 'highlightCol' : ''
            },
            {
              id: 'jitter_min',
              title: 'Jitter',
              type: 'text',
              sub_text: 'Min (ms)'
            },
            {
              id: 'jitter_max',
              title: 'Jitter',
              type: 'text',
              sub_text: 'Max (ms)'
            },
            {
              id: 'jitter_avg',
              title: 'Jitter',
              type: 'text',
              sub_text: 'Avg (ms)',
              class: colName === 'jitter_avg' ? 'highlightCol' : ''
            },
            {
              id: 'sequence_count',
              title: 'Out of Sequence',
              type: 'text',
              sub_text: 'Count'
            },
            {
              id: 'duplicate_count',
              title: 'Duplicate',
              type: 'text',
              sub_text: 'Count'
            },
            {
              id: 'timing',
              title: 'Timing',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'analytics-reports': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'name',
              title: 'Report Name',
              type: 'text-link'
            },
            {
              id: 'createdBy',
              title: 'Created By',
              type: 'text-link'
            },
            {
              id: 'date',
              title: 'Date',
              type: 'text-link'
            },
            {
              id: 'schedule',
              title: 'Schedule',
              type: 'text'
            },
            {
              id: 'destinations',
              title: 'Destinations',
              type: 'text-link'
            },
            {
              id: 'icon',
              title: '',
              type: 'mat-action-icon'
            }
          ],
          data: []
        };
      }
      case 'sla-profiles': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'name',
              title: 'Name',
              type: 'text'
            },
            {
              id: 'description',
              title: 'Description',
              type: 'text'
            },
            {
              id: 'nearFar',
              title: 'Near-Far',
              type: 'mat-icon'
            },
            {
              id: 'farNear',
              title: 'Far-Near',
              type: 'mat-icon'
            },
            {
              id: 'roundTrip',
              title: 'Round-Trip',
              type: 'mat-icon'
            },
            {
              id: 'flows',
              title: 'Flows',
              type: 'text'
            },
            {
              id: 'shortterm',
              title: 'Short Term Value',
              type: 'mat-icon'
            },
            {
              id: 'longterm',
              title: 'Long Term Value',
              type: 'mat-icon'
            },
            {
              id: 'actionMenu',
              title: 'Action',
              type: 'action-icon'
            }
          ],
          data: []
        };
      }
      case 'run_report_view': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'Flow_ID',
              title: 'Flow',
              type: 'text'
            },
            {
              id: 'SLA',
              title: 'SLA',
              type: 'text'
            },
            {
              id: 'availability',
              title: 'Availability',
              type: 'text'
            },
            {
              id: 'UAS',
              title: 'UAS',
              type: 'text'
            },
            {
              id: 'FLR',
              title: 'FLR',
              type: 'text'
            },
            {
              id: 'OOS',
              title: 'Out of Sequence',
              type: 'text'
            },
            {
              id: 'duplicate',
              title: 'Duplicate',
              type: 'text'
            },
            {
              id: 'ifdv_avg',
              title: 'IFDV Avg',
              type: 'text'
            },
            {
              id: 'ifdv_max',
              title: 'IFDV Max',
              type: 'text'
            },
            {
              id: 'fd_avg',
              title: 'FD Avg',
              type: 'text'
            },
            {
              id: 'fd_max',
              title: 'FD Max',
              type: 'text'
            },
            {
              id: 'circuit_name',
              title: 'Circuit Name',
              type: 'text'
            },
            {
              id: 'customer',
              title: 'Customer',
              type: 'text'
            },
            {
              id: 'sla_profile',
              title: 'SLA Profile',
              type: 'text'
            }
          ],
          data: []
        };
      }
      case 'flows_view': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              id: 'name',
              title: 'Flow Name',
              type: 'text'
            },
            {
              id: 'reporting',
              title: 'Reporting',
              type: 'mat-icon'
            },
            {
              id: 'notDefined',
              title: 'Not Defined',
              type: 'mat-icon'
            },
            {
              id: 'muted',
              title: 'Flow Muted',
              type: 'mat-icon'
            },
            {
              id: 'alarm_muted',
              title: 'Alarm Muted',
              type: 'mat-icon'
            },
            {
              id: 'type',
              title: 'Reflector Type',
              type: 'text'
            },
            {
              id: 'sla_profile',
              title: 'SLA Profile',
              type: 'text'
            },
            {
              id: 'near_endpoint',
              title: 'Near Endpoint',
              type: 'text'
            },
            {
              id: 'far_endpoint',
              title: 'Far Endpoint',
              type: 'text'
            },
            {
              id: 'remarks',
              title: 'Remarks',
              type: 'text'
            },
            {
              id: 'actionMenu',
              title: 'Actions',
              type: 'action-icon',
              disableSorting: true
            }
          ],
          data: []
        };
      }
    }
  }

  // eslint-disable-next-line
  getExpressionFromTokens(tokens: any): any {
    const expressions = [];
    tokens.forEach(token => {
      expressions.push(token.keyword + ' ' + token.operator + ' ' + "'" + token.operands + "'");
    });
    return expressions;
  }

  // eslint-disable-next-line
  getOperatorsFromTokens(tokens: any): any {
    const operators = [];
    tokens.forEach(token => {
      if (token.logicalOp) {
        operators.push(token.logicalOp);
      }
    });
    return operators;
  }

  setMapType(mapType: MapType): void {
    this.mapType = mapType;
    sessionStorage.setItem('mapType', this.mapType);
  }

  getMapType(): string {
    return this.mapType || 'Road Map view';
  }

  createWildcardmatchString(value: string): string {
    return value.replace('*:', '').trim();
  }

  getSavedFilters$(): Observable<any> {
    const url = '/tags/tagfilters';
    return this.apiService.get$<any>(url, {});
  }

  saveFilters$(body: Record<string, string>): Observable<any> {
    const url = '/tags/tagfilters';
    return this.apiService.post$<any>(url, body);
  }

  removeFilter$(id: number): Observable<any> {
    return this.apiService.delete$<any>(`/tags/tagfilters/${id}`);
  }

  createScoreSVG(score: number): Record<string, unknown> {
    if (score === -1) {
      return {
        svg: `<svg style="transform: scale(.8);">
      <path d="M12 0a12 12 0 0 1 12 12a12 12 0 0 1-12 12A12 12 0 0 1 0 12A12 12 0 0 1 12 0m0 2A10 10 0 0 0 2 12c0 2.4.85 4.6 2.26 6.33L18.33 4.26A9.984 9.984 0 0 0 12 2m0 20a10 10 0 0 0 10-10c0-2.4-.85-4.6-2.26-6.33L5.67 19.74A9.984 9.984 0 0 0 12 22z" fill="#d50032"></path>
    </svg>`,
        height: 24,
        margin: [10, 10, 10, 10]
      };
    } else {
      return {
        svg: `<svg width="20" height="20">
        <circle cx="10" cy="10" r="10" fill="${this.getScoreColor(score)}"/>
        <text x="50%" y="55%" text-anchor="middle" fill="white"
           font-size="10px" font-family="Arial" dy=".3em">${score}</text>
      </svg>`,
        margin: [10, 10, 10, 10]
      };
    }
  }

  getScoreColor(score: number): string {
    switch (score) {
      case 10:
        return '#00986b';
      case 9:
        return '#54a548';
      case 8:
        return '#abb223';
      case 7:
        return '#ffbf00';
      case 6:
        return '#fcaa01';
      case 5:
        return '#f99601';
      case 4:
        return '#f68202';
      case 3:
        return '#f0660a';
      case 2:
        return '#e74417';
      case 1:
        return '#de2225';
      case 0:
        return '#d50032';
    }
  }

  createThresholdSVG(
    entity: { [x: string]: string | any[] },
    value: number,
    rtKey: string,
    NFKey: string,
    FNKey: string,
    twoWay?: boolean
  ): Record<string, unknown> {
    value = value || value === 0 ? value : null;
    const svg = `<svg style="transform: scale(.3);">
                 <svg y="5">
                   <circle fill="${this.getThresholdColor(
                     entity,
                     rtKey
                   )}" cx="20.00" cy="7.50" r="6.75"/>
                   <path fill="${this.getThresholdColor(entity, FNKey)}"
                      d="M 36.771814,0 V 15 L 34.405679,13.073301 26.771814,7.5 34.405679,1.926153 Z"/>
                   <path fill="${this.getThresholdColor(entity, NFKey)}"
                      d="M 3.1784244,0 V 15 L 5.5445594,13.073584 13.178424,7.5 5.5445594,1.9264361 Z"/>
                 </svg>
                 <text x="45" y="25" font-size="2em">${value || value === 0 ? value : ''}</text>
               </svg>`;

    if (twoWay) {
      return {
        svg: svg,
        margin: [0, 10, 0, 0]
      };
    } else {
      return {
        svg: svg,
        height: 25,
        margin: [0, 5, 0, 0]
      };
    }
  }

  isUserAuthorized(value: string): boolean {
    return value ? this.userAuth.isAuthedFor(value) : true;
  }

  getThresholdColor(element: { [x: string]: string | any[] }, column: string): string {
    if (Array.isArray(element[column])) {
      if (element[column][1] === 10) {
        return '#d50032';
      } else if (element[column][1] === 1) {
        return '#ffbf00';
      } else if (element[column][1] === 0) {
        return '#fff';
      } else if (element[column].length === 0) {
        return '#fff';
      }
      return '#e5ecf5';
    }
    return '#fff';
  }

  getType(type: string): string {
    switch (type) {
      case 'flows_view_export':
      case 'flows_view_export-csv':
      case 'flows_view_export-csv-oneway':
        return 'flows_table_view';
      case 'flow_view_export-csv':
      case 'flow_view_export':
      case 'flow_view_export-csv-oneway':
        return 'flow_detail_table_view';
      default:
        return type;
    }
  }

  getArrayDifference(array1: FilterExpr[], array2: FilterExpr[], props: string[]): any[] {
    return array1
      .filter(function (o1) {
        return !array2.some(function (o2) {
          return o1.key === o2.key && o1.value === o2.value;
        });
      })
      .map(function (o) {
        return props.reduce(function (newo, name) {
          newo[name] = o[name];
          return newo;
        }, {});
      });
  }

  setHighlightClass(level: string, priorCondition: boolean): string {
    if (priorCondition) {
      return 'whiteRow';
    }
    switch (level) {
      case 'Critical': {
        return 'criticalRow';
      }
      case 'Warning': {
        return 'warningRow';
      }
      case 'Normal': {
        return 'normalRow';
      }
    }
  }

  formatAlarmValues(key: string, value: number): any {
    if (key.indexOf('jitter') !== -1 || key.indexOf('latency') !== -1) {
      return this.getKpiValue(value, TableDataFormat.MICRO_TO_MILLI, ViewMode.TABLE);
    } else if (key.indexOf('loss') !== -1 || key.indexOf('availability') !== -1) {
      return this.getKpiValue(value, TableDataFormat.PERCENTAGE, ViewMode.TABLE);
    } else {
      return this.getKpiValue(value, TableDataFormat.NUMBER, ViewMode.TABLE);
    }
  }

  private getTableObject(objectName, entity, isUtc?: boolean): ITableObject {
    let result: any = {};
    switch (objectName) {
      case 'flows_table_view': {
        const oneWayData = [
          {
            inner_Flow_ID: 'NF',
            inner_availability: entity.AvailabilityNF,
            inner_loss: entity.Flr1wayAvgNFThreshold,
            inner_latency_min: entity.Fd1wayMinNF,
            inner_latency_max: entity.Fd1wayMaxNFThreshold,
            inner_latency_avg: entity.Fd1wayAvgNFThreshold,
            inner_jitter_min: entity.Ifdv1wayMinNF,
            inner_jitter_max: entity.Ifdv1wayMaxNFThreshold,
            inner_jitter_avg: entity.Ifdv1wayAvgNFThreshold,
            inner_duplicate_count: entity.DupCountNF,
            inner_sequence_count: entity.OOOPacketCountNF,
            inner_timing: this.getTimingValue(entity.timing_count),
            Flow_ID: entity.flow_name,
            sla_profile: entity.sla_profile_name
          },
          {
            inner_Flow_ID: 'FN',
            inner_availability: entity.AvailabilityFN,
            inner_loss: entity.Flr1wayAvgFNThreshold,
            inner_latency_min: entity.Fd1wayMinFN,
            inner_latency_max: entity.Fd1wayMaxFNThreshold,
            inner_latency_avg: entity.Fd1wayAvgFNThreshold,
            inner_jitter_min: entity.Ifdv1wayMinFN,
            inner_jitter_max: entity.Ifdv1wayMaxFNThreshold,
            inner_jitter_avg: entity.Ifdv1wayAvgFNThreshold,
            inner_duplicate_count: entity.DupCountFN,
            inner_sequence_count: entity.OOOPacketCountFN,
            inner_timing: this.getTimingValue(entity.timing_count),
            Flow_ID: entity.flow_name,
            sla_profile: entity.sla_profile_name
          }
        ];
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        result = {
          id: entity.flow_name,
          Flow_ID: entity.flow_name,
          score: Number(scoreVal),
          class: this.getScoreStatus(scoreVal),
          availability: entity.Availability,
          loss: entity.Flr2wayAvgThreshold,
          latency_min: entity.Fd2wayMin,
          latency_max: entity.Fd2wayMaxThreshold,
          latency_avg: entity.Fd2wayAvgThreshold,
          jitter_min: entity.Ifdv2wayMin,
          jitter_max: entity.Ifdv2wayMaxThreshold,
          jitter_avg: entity.Ifdv2wayAvgThreshold,
          duplicate_count: entity.DupCount,
          sequence_count: entity.OOOPacketCount,
          timing: this.getTimingValue(entity.timing_count),
          sla_profile: entity.sla_profile_name,
          actions: [
            {
              id: 'show_map',
              option: 'Show on map',
              disable: false,
              class: 'mdi mdi-earth'
            },
            {
              id: 'add_compare',
              option: 'Add to compare',
              disable: false,
              class: 'mdi mdi-compare'
            },
            {
              id: 'mute',
              option: 'Mute flow',
              disable: !this.isUserAuthorized('MANAGE_PERMISSIONS'),
              class: 'mdi mdi-volume-off icon'
            },
            {
              id: 'mute_alarms',
              option: 'Mute alarms',
              disable:
                (entity.mute_alarm && entity.mute_alarm === 'mute') ||
                !this.isUserAuthorized('MANAGE_PERMISSIONS'),
              class: 'mdi mdi-bell-off icon'
            },
            {
              id: 'unmute_alarms',
              option: 'Unmute alarms',
              disable: !entity.mute_alarm || !this.isUserAuthorized('MANAGE_PERMISSIONS'),
              class: 'mdi mdi-bell icon'
            }
          ],
          oneWayData: oneWayData
        };
        break;
      }
      case 'flows_view_export-csv-oneway': {
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        result = {
          Score: scoreVal,
          Flow: entity.flow_name,
          'Availability (%)': Array.isArray(entity.Availability)
            ? entity.Availability[0]
            : entity.Availability,
          'Loss (%)': Array.isArray(entity.Flr2wayAvgThreshold)
            ? entity.Flr2wayAvgThreshold[0]
            : entity.Flr2wayAvgThreshold,
          'Latency Min (ms)': Array.isArray(entity.Fd2wayMin)
            ? entity.Fd2wayMin[0]
            : entity.Fd2wayMin,
          'Latency Max (ms)': Array.isArray(entity.Fd2wayMaxThreshold)
            ? entity.Fd2wayMaxThreshold[0]
            : entity.Fd2wayMaxThreshold,
          'Latency Avg (ms)': Array.isArray(entity.Fd2wayAvgThreshold)
            ? entity.Fd2wayAvgThreshold[0]
            : entity.Fd2wayAvgThreshold,
          'Jitter Min (ms)': Array.isArray(entity.Ifdv2wayMin)
            ? entity.Ifdv2wayMin[0]
            : entity.Ifdv2wayMin,
          'Jitter Max (ms)': Array.isArray(entity.Ifdv2wayMaxThreshold)
            ? entity.Ifdv2wayMaxThreshold[0]
            : entity.Ifdv2wayMaxThreshold,
          'Jitter Avg (ms)': Array.isArray(entity.Ifdv2wayAvgThreshold)
            ? entity.Ifdv2wayAvgThreshold[0]
            : entity.Ifdv2wayAvgThreshold,
          'Duplicate Count': entity.DupCount,
          'Out of Sequence': entity.OOOPacketCount,
          Timing: this.getTimingValue(entity.timing_count),
          'NF Availability (%)': Array.isArray(entity.AvailabilityNF)
            ? entity.AvailabilityNF[0]
            : entity.AvailabilityNF,
          'NF Loss (%)': Array.isArray(entity.Flr1wayAvgNFThreshold)
            ? entity.Flr1wayAvgNFThreshold[0]
            : entity.Flr1wayAvgNFThreshold,
          'NF Latency Min (ms)': Array.isArray(entity.Fd1wayMinNF)
            ? entity.Fd1wayMinNF[0]
            : entity.Fd1wayMinNF,
          'NF Latency Max (ms)': Array.isArray(entity.Fd1wayMaxNFThreshold)
            ? entity.Fd1wayMaxNFThreshold[0]
            : entity.Fd1wayMaxNFThreshold,
          'NF Latency Avg (ms)': Array.isArray(entity.Fd1wayAvgNFThreshold)
            ? entity.Fd1wayAvgNFThreshold[0]
            : entity.Fd1wayAvgNFThreshold,
          'NF Jitter Min (ms)': Array.isArray(entity.Ifdv1wayMinNF)
            ? entity.Ifdv1wayMinNF[0]
            : entity.Ifdv1wayMinNF,
          'NF Jitter Max (ms)': Array.isArray(entity.Ifdv1wayMaxNFThreshold)
            ? entity.Ifdv1wayMaxNFThreshold[0]
            : entity.Ifdv1wayMaxNFThreshold,
          'NF Jitter Avg (ms)': Array.isArray(entity.Ifdv1wayAvgNFThreshold)
            ? entity.Ifdv1wayAvgNFThreshold[0]
            : entity.Ifdv1wayAvgNFThreshold,
          'NF Duplicate Count': entity.DupCountNF,
          'NF Out of Sequence': entity.OOOPacketCountNF,
          'NF Timing': this.getTimingValue(entity.timing_count),
          'FN Availability (%)': Array.isArray(entity.AvailabilityFN)
            ? entity.AvailabilityFN[0]
            : entity.AvailabilityFN,
          'FN Loss (%)': Array.isArray(entity.Flr1wayAvgFNThreshold)
            ? entity.Flr1wayAvgFNThreshold[0]
            : entity.Flr1wayAvgFNThreshold,
          'FN Latency Min (ms)': Array.isArray(entity.Fd1wayMinFN)
            ? entity.Fd1wayMinFN[0]
            : entity.Fd1wayMinFN,
          'FN Latency Max (ms)': Array.isArray(entity.Fd1wayMaxFNThreshold)
            ? entity.Fd1wayMaxFNThreshold[0]
            : entity.Fd1wayMaxFNThreshold,
          'FN Latency Avg (ms)': Array.isArray(entity.Fd1wayAvgFNThreshold)
            ? entity.Fd1wayAvgFNThreshold[0]
            : entity.Fd1wayAvgFNThreshold,
          'FN Jitter Min (ms)': Array.isArray(entity.Ifdv1wayMinFN)
            ? entity.Ifdv1wayMinFN[0]
            : entity.Ifdv1wayMinFN,
          'FN Jitter Max (ms)': Array.isArray(entity.Ifdv1wayMaxFNThreshold)
            ? entity.Ifdv1wayMaxFNThreshold[0]
            : entity.Ifdv1wayMaxFNThreshold,
          'FN Jitter Avg (ms)': Array.isArray(entity.Ifdv1wayAvgFNThreshold)
            ? entity.Ifdv1wayAvgFNThreshold[0]
            : entity.Ifdv1wayAvgFNThreshold,
          'FN Duplicate Count': entity.DupCountFN,
          'FN Out of Sequence': entity.OOOPacketCountFN,
          'FN Timing': this.getTimingValue(entity.timing_count)
        };
        for (const key in Object.keys(result)) {
          if (!result[key] && result[key] !== 0) {
            result[key] = '';
          }
        }
        break;
      }
      case 'flows_view_export-csv': {
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        result = {
          Score: scoreVal,
          Flow: entity.flow_name,
          'Availability (%)': Array.isArray(entity.Availability)
            ? entity.Availability[0]
            : entity.Availability,
          'Loss (%)': Array.isArray(entity.Flr2wayAvgThreshold)
            ? entity.Flr2wayAvgThreshold[0]
            : entity.Flr2wayAvgThreshold,
          'Latency Min (ms)': Array.isArray(entity.Fd2wayMin)
            ? entity.Fd2wayMin[0]
            : entity.Fd2wayMin,
          'Latency Max (ms)': Array.isArray(entity.Fd2wayMaxThreshold)
            ? entity.Fd2wayMaxThreshold[0]
            : entity.Fd2wayMaxThreshold,
          'Latency Avg (ms)': Array.isArray(entity.Fd2wayAvgThreshold)
            ? entity.Fd2wayAvgThreshold[0]
            : entity.Fd2wayAvgThreshold,
          'Jitter Min (ms)': Array.isArray(entity.Ifdv2wayMin)
            ? entity.Ifdv2wayMin[0]
            : entity.Ifdv2wayMin,
          'Jitter Max (ms)': Array.isArray(entity.Ifdv2wayMaxThreshold)
            ? entity.Ifdv2wayMaxThreshold[0]
            : entity.Ifdv2wayMaxThreshold,
          'Jitter Avg (ms)': Array.isArray(entity.Ifdv2wayAvgThreshold)
            ? entity.Ifdv2wayAvgThreshold[0]
            : entity.Ifdv2wayAvgThreshold,
          'Duplicate Count': entity.DupCount,
          'Out of Sequence': entity.OOOPacketCount,
          Timing: this.getTimingValue(entity.timing_count)
        };
        for (const key in Object.keys(result)) {
          if (!result[key] && result[key] !== 0) {
            result[key] = '';
          }
        }
        break;
      }
      case 'flows_view_export': {
        const oneWayData = [
          {
            Score: { text: '' },
            Flow: {
              svg: this.near_far_svg,
              alignment: 'center',
              margin: [40, 3, 0, 0]
            },
            'Availability (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.AvailabilityNF)
                ? entity.AvailabilityNF[0]
                : entity.AvailabilityNF,
              'AvailabilityNF',
              'AvailabilityNF',
              ''
            ),
            'Loss (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Flr1wayAvgNFThreshold)
                ? entity.Flr1wayAvgNFThreshold[0]
                : entity.Flr1wayAvgNFThreshold,
              'Flr1wayAvgNFThreshold',
              'Flr1wayAvgNFThreshold',
              ''
            ),
            'Latency Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMinNF) ? entity.Fd1wayMinNF[0] : entity.Fd1wayMinNF,
              'Fd1wayMinNF',
              'Fd1wayMinNF',
              ''
            ),
            'Latency Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMaxNFThreshold)
                ? entity.Fd1wayMaxNFThreshold[0]
                : entity.Fd1wayMaxNFThreshold,
              'Fd1wayMaxNFThreshold',
              'Fd1wayMaxNFThreshold',
              ''
            ),
            'Latency Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayAvgNFThreshold)
                ? entity.Fd1wayAvgNFThreshold[0]
                : entity.Fd1wayAvgNFThreshold,
              'Fd1wayAvgNFThreshold',
              'Fd1wayAvgNFThreshold',
              ''
            ),
            'Jitter Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMinNF) ? entity.Ifdv1wayMinNF[0] : entity.Ifdv1wayMinNF,
              'Ifdv1wayMinNF',
              'Ifdv1wayMinNF',
              ''
            ),
            'Jitter Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMaxNFThreshold)
                ? entity.Ifdv1wayMaxNFThreshold[0]
                : entity.Ifdv1wayMaxNFThreshold,
              'Ifdv1wayMaxNFThreshold',
              'Ifdv1wayMaxNFThreshold',
              ''
            ),
            'Jitter Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayAvgNFThreshold)
                ? entity.Ifdv1wayAvgNFThreshold[0]
                : entity.Ifdv1wayAvgNFThreshold,
              'Ifdv1wayAvgNFThreshold',
              'Ifdv1wayAvgNFThreshold',
              ''
            ),
            MOS: this.createThresholdSVG(entity, entity.qm_1way_mos_n_f, '', '', '', false),
            RFactor: this.createThresholdSVG(
              entity,
              entity.qm_1way_r_factor_n_f,
              '',
              '',
              '',
              false
            ),
            'Duplicate Count': this.createThresholdSVG(
              entity,
              entity.DupCountNF,
              '',
              'DupCountNF',
              ''
            ),
            'Out of Sequence': this.createThresholdSVG(
              entity,
              entity.OOOPacketCountNF,
              '',
              'OOOPacketCountNF',
              ''
            ),
            Timing: {
              text: this.getTimingValue(entity.timing_count),
              alignment: 'center',
              margin: [0, 3, 0, 0],
              fontSize: 10
            }
          },
          {
            Score: { text: '' },
            Flow: {
              svg: this.far_near_svg,
              alignment: 'center',
              margin: [40, 3, 0, 0]
            },
            'Availability (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.AvailabilityFN)
                ? entity.AvailabilityFN[0]
                : entity.AvailabilityFN,
              'AvailabilityFN',
              '',
              'AvailabilityFN'
            ),
            'Loss (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Flr1wayAvgFNThreshold)
                ? entity.Flr1wayAvgFNThreshold[0]
                : entity.Flr1wayAvgFNThreshold,
              'Flr1wayAvgFNThreshold',
              '',
              'Flr1wayAvgFNThreshold'
            ),
            'Latency Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMinFN) ? entity.Fd1wayMinFN[0] : entity.Fd1wayMinFN,
              'Fd1wayMinFN',
              '',
              'Fd1wayMinFN'
            ),
            'Latency Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMaxFNThreshold)
                ? entity.Fd1wayMaxFNThreshold[0]
                : entity.Fd1wayMaxFNThreshold,
              'Fd1wayMaxFNThreshold',
              '',
              'Fd1wayMaxFNThreshold'
            ),
            'Latency Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayAvgFNThreshold)
                ? entity.Fd1wayAvgFNThreshold[0]
                : entity.Fd1wayAvgFNThreshold,
              'Fd1wayAvgFNThreshold',
              '',
              'Fd1wayAvgFNThreshold'
            ),
            'Jitter Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMinFN) ? entity.Ifdv1wayMinFN[0] : entity.Ifdv1wayMinFN,
              'Ifdv1wayMinFN',
              '',
              'Ifdv1wayMinFN'
            ),
            'Jitter Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMaxFNThreshold)
                ? entity.Ifdv1wayMaxFNThreshold[0]
                : entity.Ifdv1wayMaxFNThreshold,
              'Ifdv1wayMaxFNThreshold',
              '',
              'Ifdv1wayMaxFNThreshold'
            ),
            'Jitter Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayAvgFNThreshold)
                ? entity.Ifdv1wayAvgFNThreshold[0]
                : entity.Ifdv1wayAvgFNThreshold,
              'Ifdv1wayAvgFNThreshold',
              '',
              'Ifdv1wayAvgFNThreshold'
            ),
            MOS: this.createThresholdSVG(entity, entity.qm_1way_mos_f_n, '', '', '', false),
            RFactor: this.createThresholdSVG(
              entity,
              entity.qm_1way_r_factor_f_n,
              '',
              '',
              '',
              false
            ),
            'Duplicate Count': this.createThresholdSVG(
              entity,
              entity.DupCountFN,
              '',
              '',
              'DupCountFN'
            ),
            'Out of Sequence': this.createThresholdSVG(
              entity,
              entity.OOOPacketCountFN,
              '',
              '',
              'OOOPacketCountFN'
            ),
            Timing: {
              text: this.getTimingValue(entity.timing_count),
              alignment: 'center',
              margin: [0, 3, 0, 0],
              fontSize: 10
            }
          }
        ];
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        result = {
          Score: this.createScoreSVG(scoreVal),
          Flow: { text: entity.flow_name, alignment: 'center', fontSize: 10 },
          'Availability (%)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Availability) ? entity.Availability[0] : entity.Availability,
            'Availability',
            'AvailabilityNF',
            'AvailabilityFN',
            true
          ),
          'Loss (%)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Flr2wayAvgThreshold)
              ? entity.Flr2wayAvgThreshold[0]
              : entity.Flr2wayAvgThreshold,
            'Flr2wayAvgThreshold',
            'Flr1wayAvgNFThreshold',
            'Flr1wayAvgFNThreshold',
            true
          ),
          'Latency Min (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Fd2wayMin) ? entity.Fd2wayMin[0] : entity.Fd2wayMin,
            'Fd2wayMin',
            'Fd1wayMinNF',
            'Fd1wayMinFN',
            true
          ),
          'Latency Max (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Fd2wayMaxThreshold)
              ? entity.Fd2wayMaxThreshold[0]
              : entity.Fd2wayMaxThreshold,
            'Fd2wayMaxThreshold',
            'Fd1wayMaxNFThreshold',
            'Fd1wayMaxFNThreshold',
            true
          ),
          'Latency Avg (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Fd2wayAvgThreshold)
              ? entity.Fd2wayAvgThreshold[0]
              : entity.Fd2wayAvgThreshold,
            'Fd2wayAvgThreshold',
            'Fd1wayAvgNFThreshold',
            'Fd1wayAvgFNThreshold',
            true
          ),
          'Jitter Min (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Ifdv2wayMin) ? entity.Ifdv2wayMin[0] : entity.Ifdv2wayMin,
            'Ifdv2wayMin',
            'Ifdv1wayMinNF',
            'Ifdv1wayMinFN',
            true
          ),
          'Jitter Max (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Ifdv2wayMaxThreshold)
              ? entity.Ifdv2wayMaxThreshold[0]
              : entity.Ifdv2wayMaxThreshold,
            'Ifdv2wayMaxThreshold',
            'Ifdv1wayMaxNFThreshold',
            'Ifdv1wayMaxFNThreshold',
            true
          ),
          'Jitter Avg (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Ifdv2wayAvgThreshold)
              ? entity.Ifdv2wayAvgThreshold[0]
              : entity.Ifdv2wayAvgThreshold,
            'Ifdv2wayAvgThreshold',
            'Ifdv1wayAvgNFThreshold',
            'Ifdv1wayAvgFNThreshold',
            true
          ),
          'Duplicate Count': this.createThresholdSVG(
            entity,
            entity.DupCount,
            'DupCount',
            'DupCountNF',
            'DupCountFN',
            true
          ),
          'Out of Sequence': this.createThresholdSVG(
            entity,
            entity.OOOPacketCount,
            'OOOPacketCount',
            'OOOPacketCountNF',
            'OOOPacketCountFN',
            true
          ),
          Timing: {
            text: this.getTimingValue(entity.timing_count),
            alignment: 'center',
            margin: [0, 8, 0, 0],
            fontSize: 10
          },
          oneWayData: oneWayData
        };
        for (const key in Object.keys(result)) {
          if (!result[key] && result[key] !== 0) {
            result[key] = '';
          }
        }
        break;
      }
      case 'flow_view_export-csv': {
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        const dt = new Date(parseInt(entity.startTime));
        const fullDate = this.getReportDate(dt, isUtc);
        result = {
          Score: scoreVal,
          'Start time': fullDate,
          'Availability (%)': Array.isArray(entity.Availability)
            ? entity.Availability[0]
            : entity.Availability,
          'Loss (%)': Array.isArray(entity.Flr2wayAvgThreshold)
            ? entity.Flr2wayAvgThreshold[0]
            : entity.Flr2wayAvgThreshold,
          'Latency Min (ms)': Array.isArray(entity.Fd2wayMin)
            ? entity.Fd2wayMin[0]
            : entity.Fd2wayMin,
          'Latency Max (ms)': Array.isArray(entity.Fd2wayMaxThreshold)
            ? entity.Fd2wayMaxThreshold[0]
            : entity.Fd2wayMaxThreshold,
          'Latency Avg (ms)': Array.isArray(entity.Fd2wayAvgThreshold)
            ? entity.Fd2wayAvgThreshold[0]
            : entity.Fd2wayAvgThreshold,
          'Jitter Min (ms)': Array.isArray(entity.Ifdv2wayMin)
            ? entity.Ifdv2wayMin[0]
            : entity.Ifdv2wayMin,
          'Jitter Max (ms)': Array.isArray(entity.Ifdv2wayMaxThreshold)
            ? entity.Ifdv2wayMaxThreshold[0]
            : entity.Ifdv2wayMaxThreshold,
          'Jitter Avg (ms)': Array.isArray(entity.Ifdv2wayAvgThreshold)
            ? entity.Ifdv2wayAvgThreshold[0]
            : entity.Ifdv2wayAvgThreshold,
          'Duplicate Count': entity.DupCount,
          'Out of Sequence': entity.OOOPacketCount,
          Timing: this.getTimingValue(entity.timing_count)
        };
        for (const key in Object.keys(result)) {
          if (!result[key] && result[key] !== 0) {
            result[key] = '';
          }
        }
        break;
      }
      case 'flow_view_export-csv-oneway': {
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        const dt = new Date(parseInt(entity.period_start_time));
        const fullDate = this.getReportDate(dt, isUtc);
        result = {
          Score: scoreVal,
          'Start time': fullDate,
          'Availability (%)': Array.isArray(entity.Availability)
            ? entity.Availability[0]
            : entity.Availability,
          'Loss (%)': Array.isArray(entity.Flr2wayAvgThreshold)
            ? entity.Flr2wayAvgThreshold[0]
            : entity.Flr2wayAvgThreshold,
          'Latency Min (ms)': Array.isArray(entity.Fd2wayMin)
            ? entity.Fd2wayMin[0]
            : entity.Fd2wayMin,
          'Latency Max (ms)': Array.isArray(entity.Fd2wayMaxThreshold)
            ? entity.Fd2wayMaxThreshold[0]
            : entity.Fd2wayMaxThreshold,
          'Latency Avg (ms)': Array.isArray(entity.Fd2wayAvgThreshold)
            ? entity.Fd2wayAvgThreshold[0]
            : entity.Fd2wayAvgThreshold,
          'Jitter Min (ms)': Array.isArray(entity.Ifdv2wayMin)
            ? entity.Ifdv2wayMin[0]
            : entity.Ifdv2wayMin,
          'Jitter Max (ms)': Array.isArray(entity.Ifdv2wayMaxThreshold)
            ? entity.Ifdv2wayMaxThreshold[0]
            : entity.Ifdv2wayMaxThreshold,
          'Jitter Avg (ms)': Array.isArray(entity.Ifdv2wayAvgThreshold)
            ? entity.Ifdv2wayAvgThreshold[0]
            : entity.Ifdv2wayAvgThreshold,
          'Duplicate Count': entity.DupCount,
          'Out of Sequence': entity.OOOPacketCount,
          Timing: this.getTimingValue(entity.timing_count),
          'NF Availability (%)': Array.isArray(entity.AvailabilityNF)
            ? entity.AvailabilityNF[0]
            : entity.AvailabilityNF,
          'NF Loss (%)': Array.isArray(entity.Flr1wayAvgNFThreshold)
            ? entity.Flr1wayAvgNFThreshold[0]
            : entity.Flr1wayAvgNFThreshold,
          'NF Latency Min (ms)': Array.isArray(entity.Fd1wayMinNF)
            ? entity.Fd1wayMinNF[0]
            : entity.Fd1wayMinNF,
          'NF Latency Max (ms)': Array.isArray(entity.Fd1wayMaxNFThreshold)
            ? entity.Fd1wayMaxNFThreshold[0]
            : entity.Fd1wayMaxNFThreshold,
          'NF Latency Avg (ms)': Array.isArray(entity.Fd1wayAvgNFThreshold)
            ? entity.Fd1wayAvgNFThreshold[0]
            : entity.Fd1wayAvgNFThreshold,
          'NF Jitter Min (ms)': Array.isArray(entity.Ifdv1wayMinNF)
            ? entity.Ifdv1wayMinNF[0]
            : entity.Ifdv1wayMinNF,
          'NF Jitter Max (ms)': Array.isArray(entity.Ifdv1wayMaxNFThreshold)
            ? entity.Ifdv1wayMaxNFThreshold[0]
            : entity.Ifdv1wayMaxNFThreshold,
          'NF Jitter Avg (ms)': Array.isArray(entity.Ifdv1wayAvgNFThreshold)
            ? entity.Ifdv1wayAvgNFThreshold[0]
            : entity.Ifdv1wayAvgNFThreshold,
          'NF Duplicate Count': entity.DupCountNF,
          'NF Out of Sequence': entity.OOOPacketCountNF,
          'NF Timing': this.getTimingValue(entity.timing_count),
          'FN Availability (%)': Array.isArray(entity.AvailabilityFN)
            ? entity.AvailabilityFN[0]
            : entity.AvailabilityFN,
          'FN Loss (%)': Array.isArray(entity.Flr1wayAvgFNThreshold)
            ? entity.Flr1wayAvgFNThreshold[0]
            : entity.Flr1wayAvgFNThreshold,
          'FN Latency Min (ms)': Array.isArray(entity.Fd1wayMinFN)
            ? entity.Fd1wayMinFN[0]
            : entity.Fd1wayMinFN,
          'FN Latency Max (ms)': Array.isArray(entity.Fd1wayMaxFNThreshold)
            ? entity.Fd1wayMaxFNThreshold[0]
            : entity.Fd1wayMaxFNThreshold,
          'FN Latency Avg (ms)': Array.isArray(entity.Fd1wayAvgFNThreshold)
            ? entity.Fd1wayAvgFNThreshold[0]
            : entity.Fd1wayAvgFNThreshold,
          'FN Jitter Min (ms)': Array.isArray(entity.Ifdv1wayMinFN)
            ? entity.Ifdv1wayMinFN[0]
            : entity.Ifdv1wayMinFN,
          'FN Jitter Max (ms)': Array.isArray(entity.Ifdv1wayMaxFNThreshold)
            ? entity.Ifdv1wayMaxFNThreshold[0]
            : entity.Ifdv1wayMaxFNThreshold,
          'FN Jitter Avg (ms)': Array.isArray(entity.Ifdv1wayAvgFNThreshold)
            ? entity.Ifdv1wayAvgFNThreshold[0]
            : entity.Ifdv1wayAvgFNThreshold,
          'FN Duplicate Count': entity.DupCountFN,
          'FN Out of Sequence': entity.OOOPacketCountFN,
          'FN Timing': this.getTimingValue(entity.timing_count)
        };
        for (const key in Object.keys(result)) {
          if (!result[key] && result[key] !== 0) {
            result[key] = '';
          }
        }
        break;
      }
      case 'flow_view_export': {
        const dt = new Date(parseInt(entity.period_start_time));
        const fullDate = this.getReportDate(dt, isUtc);
        const oneWayData = [
          {
            Score: { text: '' },
            'Start time': {
              svg: this.near_far_svg,
              alignment: 'center',
              margin: [40, 3, 0, 0]
            },
            'Availability (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.AvailabilityNF)
                ? entity.AvailabilityNF[0]
                : entity.AvailabilityNF,
              'AvailabilityNF',
              'AvailabilityNF',
              ''
            ),
            'Loss (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Flr1wayAvgNFThreshold)
                ? entity.Flr1wayAvgNFThreshold[0]
                : entity.Flr1wayAvgNFThreshold,
              'Flr1wayAvgNFThreshold',
              'Flr1wayAvgNFThreshold',
              ''
            ),
            'Latency Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMinNF) ? entity.Fd1wayMinNF[0] : entity.Fd1wayMinNF,
              'Fd1wayMinNF',
              'Fd1wayMinNF',
              ''
            ),
            'Latency Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMaxNFThreshold)
                ? entity.Fd1wayMaxNFThreshold[0]
                : entity.Fd1wayMaxNFThreshold,
              'Fd1wayMaxNFThreshold',
              'Fd1wayMaxNFThreshold',
              ''
            ),
            'Latency Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayAvgNFThreshold)
                ? entity.Fd1wayAvgNFThreshold[0]
                : entity.Fd1wayAvgNFThreshold,
              'Fd1wayAvgNFThreshold',
              'Fd1wayAvgNFThreshold',
              ''
            ),
            'Jitter Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMinNF) ? entity.Ifdv1wayMinNF[0] : entity.Ifdv1wayMinNF,
              'Ifdv1wayMinNF',
              'Ifdv1wayMinNF',
              ''
            ),
            'Jitter Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMaxNFThreshold)
                ? entity.Ifdv1wayMaxNFThreshold[0]
                : entity.Ifdv1wayMaxNFThreshold,
              'Ifdv1wayMaxNFThreshold',
              'Ifdv1wayMaxNFThreshold',
              ''
            ),
            'Jitter Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayAvgNFThreshold)
                ? entity.Ifdv1wayAvgNFThreshold[0]
                : entity.Ifdv1wayAvgNFThreshold,
              'Ifdv1wayAvgNFThreshold',
              'Ifdv1wayAvgNFThreshold',
              ''
            ),
            MOS: this.createThresholdSVG(entity, entity.qm_1way_mos_n_f, '', '', '', false),
            RFactor: this.createThresholdSVG(
              entity,
              entity.qm_1way_r_factor_n_f,
              '',
              '',
              '',
              false
            ),
            'Duplicate Count': this.createThresholdSVG(
              entity,
              entity.DupCountNF,
              '',
              'DupCountNF',
              ''
            ),
            'Out of Sequence': this.createThresholdSVG(
              entity,
              entity.OOOPacketCountNF,
              '',
              'OOOPacketCountNF',
              ''
            ),
            Timing: {
              text: this.getTimingValue(entity.timing_count),
              alignment: 'center',
              margin: [0, 3, 0, 0],
              fontSize: 10
            }
          },
          {
            Score: { text: '' },
            'Start time': {
              svg: this.far_near_svg,
              alignment: 'center',
              margin: [40, 3, 0, 0]
            },
            'Availability (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.AvailabilityFN)
                ? entity.AvailabilityFN[0]
                : entity.AvailabilityFN,
              'AvailabilityFN',
              '',
              'AvailabilityFN'
            ),
            'Loss (%)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Flr1wayAvgFNThreshold)
                ? entity.Flr1wayAvgFNThreshold[0]
                : entity.Flr1wayAvgFNThreshold,
              'Flr1wayAvgFNThreshold',
              '',
              'Flr1wayAvgFNThreshold'
            ),
            'Latency Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMinFN) ? entity.Fd1wayMinFN[0] : entity.Fd1wayMinFN,
              'Fd1wayMinFN',
              '',
              'Fd1wayMinFN'
            ),
            'Latency Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayMaxFNThreshold)
                ? entity.Fd1wayMaxFNThreshold[0]
                : entity.Fd1wayMaxFNThreshold,
              'Fd1wayMaxFNThreshold',
              '',
              'Fd1wayMaxFNThreshold'
            ),
            'Latency Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Fd1wayAvgFNThreshold)
                ? entity.Fd1wayAvgFNThreshold[0]
                : entity.Fd1wayAvgFNThreshold,
              'Fd1wayAvgFNThreshold',
              '',
              'Fd1wayAvgFNThreshold'
            ),
            'Jitter Min (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMinFN) ? entity.Ifdv1wayMinFN[0] : entity.Ifdv1wayMinFN,
              'Ifdv1wayMinFN',
              '',
              'Ifdv1wayMinFN'
            ),
            'Jitter Max (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayMaxFNThreshold)
                ? entity.Ifdv1wayMaxFNThreshold[0]
                : entity.Ifdv1wayMaxFNThreshold,
              'Ifdv1wayMaxFNThreshold',
              '',
              'Ifdv1wayMaxFNThreshold'
            ),
            'Jitter Avg (ms)': this.createThresholdSVG(
              entity,
              Array.isArray(entity.Ifdv1wayAvgFNThreshold)
                ? entity.Ifdv1wayAvgFNThreshold[0]
                : entity.Ifdv1wayAvgFNThreshold,
              'Ifdv1wayAvgFNThreshold',
              '',
              'Ifdv1wayAvgFNThreshold'
            ),
            MOS: this.createThresholdSVG(entity, entity.qm_1way_mos_f_n, '', '', '', false),
            RFactor: this.createThresholdSVG(
              entity,
              entity.qm_1way_r_factor_f_n,
              '',
              '',
              '',
              false
            ),
            'Duplicate Count': this.createThresholdSVG(
              entity,
              entity.DupCountFN,
              '',
              '',
              'DupCountFN'
            ),
            'Out of Sequence': this.createThresholdSVG(
              entity,
              entity.OOOPacketCountFN,
              '',
              '',
              'OOOPacketCountFN'
            ),
            Timing: {
              text: this.getTimingValue(entity.timing_count),
              alignment: 'center',
              margin: [0, 3, 0, 0],
              fontSize: 10
            }
          }
        ];
        const validResult = 1;
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        result = {
          Score: this.createScoreSVG(scoreVal),
          'Start time': {
            text: fullDate,
            alignment: 'center',
            fontSize: 10,
            margin: [0, 10, 0, 0]
          },
          'Availability (%)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Availability) ? entity.Availability[0] : entity.Availability,
            'Availability',
            'AvailabilityNF',
            'AvailabilityFN',
            true
          ),
          'Loss (%)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Flr2wayAvgThreshold)
              ? entity.Flr2wayAvgThreshold[0]
              : entity.Flr2wayAvgThreshold,
            'Flr2wayAvgThreshold',
            'Flr1wayAvgNFThreshold',
            'Flr1wayAvgFNThreshold',
            true
          ),
          'Latency Min (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Fd2wayMin) ? entity.Fd2wayMin[0] : entity.Fd2wayMin,
            'Fd2wayMin',
            'Fd1wayMinNF',
            'Fd1wayMinFN',
            true
          ),
          'Latency Max (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Fd2wayMaxThreshold)
              ? entity.Fd2wayMaxThreshold[0]
              : entity.Fd2wayMaxThreshold,
            'Fd2wayMaxThreshold',
            'Fd1wayMaxNFThreshold',
            'Fd1wayMaxFNThreshold',
            true
          ),
          'Latency Avg (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Fd2wayAvgThreshold)
              ? entity.Fd2wayAvgThreshold[0]
              : entity.Fd2wayAvgThreshold,
            'Fd2wayAvgThreshold',
            'Fd1wayAvgNFThreshold',
            'Fd1wayAvgFNThreshold',
            true
          ),
          'Jitter Min (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Ifdv2wayMin) ? entity.Ifdv2wayMin[0] : entity.Ifdv2wayMin,
            'Ifdv2wayMin',
            'Ifdv1wayMinNF',
            'Ifdv1wayMinFN',
            true
          ),
          'Jitter Max (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Ifdv2wayMaxThreshold)
              ? entity.Ifdv2wayMaxThreshold[0]
              : entity.Ifdv2wayMaxThreshold,
            'Ifdv2wayMaxThreshold',
            'Ifdv1wayMaxNFThreshold',
            'Ifdv1wayMaxFNThreshold',
            true
          ),
          'Jitter Avg (ms)': this.createThresholdSVG(
            entity,
            Array.isArray(entity.Ifdv2wayAvgThreshold)
              ? entity.Ifdv2wayAvgThreshold[0]
              : entity.Ifdv2wayAvgThreshold,
            'Ifdv2wayAvgThreshold',
            'Ifdv1wayAvgNFThreshold',
            'Ifdv1wayAvgFNThreshold',
            true
          ),
          'Duplicate Count': this.createThresholdSVG(
            entity,
            entity.DupCount,
            'DupCount',
            'DupCountNF',
            'DupCountFN',
            true
          ),
          MOS: this.createThresholdSVG(entity, entity.qm_2way_mos, '', '', '', true),
          RFactor: this.createThresholdSVG(entity, entity.qm_2way_r_factor, '', '', '', true),
          'Out of Sequence': this.createThresholdSVG(
            entity,
            entity.OOOPacketCount,
            'OOOPacketCount',
            'OOOPacketCountNF',
            'OOOPacketCountFN',
            true
          ),
          Timing: {
            text: this.getTimingValue(entity.timing_count),
            alignment: 'center',
            margin: [0, 8, 0, 0],
            fontSize: 10
          },
          oneWayData: oneWayData
        };
        for (const key in Object.keys(result)) {
          if (!result[key] && result[key] !== 0) {
            result[key] = '';
          }
        }
        break;
      }
      case 'flow_detail_table_view': {
        // TODO - Consider adding this to the DateFormatterService and then using it from there
        const dt = new Date(parseInt(entity.startTime));
        const fullDate =
          dt.toLocaleDateString('default', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }) +
          ' ' +
          new Date(dt).toTimeString().replace(/.*(\d{2}:\d{2}:\d{2}).*/, '$1');
        const oneWayData = [
          {
            inner_startTime: 'NF',
            inner_availability: entity.AvailabilityNF,
            inner_loss: entity.Flr1wayAvgNFThreshold,
            inner_latency_min: entity.Fd1wayMinNF,
            inner_latency_max: entity.Fd1wayMaxNFThreshold,
            inner_latency_avg: entity.Fd1wayAvgNFThreshold,
            inner_jitter_min: entity.Ifdv1wayMinNF,
            inner_jitter_max: entity.Ifdv1wayMaxNFThreshold,
            inner_jitter_avg: entity.Ifdv1wayAvgNFThreshold,
            inner_duplicate_count: entity.DupCountNF,
            inner_sequence_count: entity.OOOPacketCountNF,
            inner_timing: this.getTimingValue(entity.timing_count),
            id: entity.flow_name,
            sla_profile: entity.sla_profile_name,
            inner_mos: entity.qm_1way_mos_n_f,
            inner_rfactor: entity.qm_1way_r_factor_n_f
          },
          {
            inner_startTime: 'FN',
            inner_availability: entity.AvailabilityFN,
            inner_loss: entity.Flr1wayAvgFNThreshold,
            inner_latency_min: entity.Fd1wayMinFN,
            inner_latency_max: entity.Fd1wayMaxFNThreshold,
            inner_latency_avg: entity.Fd1wayAvgFNThreshold,
            inner_jitter_min: entity.Ifdv1wayMinFN,
            inner_jitter_max: entity.Ifdv1wayMaxFNThreshold,
            inner_jitter_avg: entity.Ifdv1wayAvgFNThreshold,
            inner_duplicate_count: entity.DupCountFN,
            inner_sequence_count: entity.OOOPacketCountFN,
            inner_timing: this.getTimingValue(entity.timing_count),
            id: entity.flow_name,
            sla_profile: entity.sla_profile_name,
            inner_mos: entity.qm_1way_mos_f_n,
            inner_rfactor: entity.qm_1way_r_factor_f_n
          }
        ];
        const scoreVal = TableDataSourceService.isValid(entity)
          ? Math.floor(entity['overall_score'])
          : -1;
        result = {
          id: entity.flow_name,
          score: Number(scoreVal),
          class: this.getScoreStatus(scoreVal),
          startTime: fullDate,
          timestamp: entity.startTime,
          availability: entity.Availability,
          loss: entity.Flr2wayAvgThreshold,
          latency_min: entity.Fd2wayMin,
          latency_max: entity.Fd2wayMaxThreshold,
          latency_avg: entity.Fd2wayAvgThreshold,
          jitter_min: entity.Ifdv2wayMin,
          jitter_max: entity.Ifdv2wayMaxThreshold,
          jitter_avg: entity.Ifdv2wayAvgThreshold,
          duplicate_count: entity.DupCountFN,
          sequence_count: entity.OOOPacketCount,
          timing: this.getTimingValue(entity.timing_count),
          oneWayData: oneWayData,
          sla_profile: entity.sla_profile_name,
          mos: entity.qm_2way_mos,
          rfactor: entity.qm_2way_r_factor
        };
        break;
      }
      case 'flows_view': {
        result = {
          rowId: entity.flow_id,
          id: entity.flow_name,
          name: entity.flow_name,
          reporting: TableDataSourceService.getIconInfo(
            !entity.reporting ? 'no' : 'yes',
            'reporting'
          ),
          notDefined: TableDataSourceService.getIconInfo(
            !entity.discovered ? 'no' : 'yes',
            'notDefined'
          ),
          muted: TableDataSourceService.getIconInfo(!entity.mute ? 'no' : 'yes', 'muted'),
          alarm_muted: TableDataSourceService.getIconInfo(
            !entity.mute_alarm ? 'no' : 'yes',
            'alarm_muted'
          ),
          reportFlag: entity.reporting,
          isDisabled: entity.discovered,
          isnotDefined: entity.discovered,
          type: entity?.reflector_type
            ? REFLECTOR_TYPES.find(type => type.value === entity.reflector_type).key
            : '',
          sla_profile: entity.sla_profile,
          near_endpoint: entity.near_endpoint,
          far_endpoint: entity.far_endpoint,
          remarks: entity.remarks,
          disableAction:
            !this.isUserAuthorized('MANAGE_PERMISSIONS') || entity.discovered === 'yes',
          actionMenu: [
            {
              option: entity.mute ? 'Unmute flow' : 'Mute flow',
              class: entity.mute ? 'mdi mdi-volume-high' : 'mdi mdi-volume-off icon'
            },
            {
              option: entity.mute_alarm ? 'Unmute alarms' : 'Mute alarms',
              class: entity.mute_alarm ? 'mdi mdi-bell' : 'mdi mdi-bell-off icon'
            },
            {
              option: 'Delete flow',
              class: 'mdi mdi-delete'
            }
          ]
        };
        break;
      }
      case 'alarms_table_view': {
        result = {
          id: entity.id,
          flow: entity.flow_name,
          periodStartTime: entity.period_start_time,
          alarmCreated: entity.created_on,
          profile: entity.profile_name,
          severity: entity.severity,
          perspective: Direction[entity.direction],
          KPIName: entity.kpi_name,
          KPIValue: this.formatAlarmValues(entity.kpi_name.toLowerCase(), entity.kpi_value),
          ThresholdValue:
            entity.threshold && entity.threshold === -1
              ? null
              : this.formatAlarmValues(entity.kpi_name.toLowerCase(), entity.threshold),
          alarm_muted: TableDataSourceService.getIconInfo(
            entity.mute_alarm ? 'yes' : 'no',
            'alarm_muted'
          ),
          highlightClass: this.setHighlightClass(entity.severity, entity.is_acknowledged),
          tickMarkColor: entity.is_acknowledged ? '#8692a2' : '#000000de',
          cellColor: '#000000de',
          disableAction: !this.isUserAuthorized('MANAGE_PERMISSIONS'),
          acknowledged: entity.is_acknowledged,
          actionMenu: [
            {
              option: 'Mute flow',
              hide: !this.isUserAuthorized('MANAGE_PERMISSIONS'),
              disable: false,
              class: 'mdi mdi-volume-off icon'
            },
            {
              option: !entity.mute_alarm ? 'Mute alarm' : 'UnMute alarm',
              hide: entity.mute_alarm || !this.isUserAuthorized('MANAGE_PERMISSIONS'),
              class: !entity.mute_alarm ? 'mdi mdi-bell-off icon' : 'mdi mdi-bell'
            },
            {
              id: 'acknowledge',
              option: 'Acknowledge alarm',
              disable: entity.is_acknowledged || !this.isUserAuthorized('MANAGE_PERMISSIONS'),
              class: 'mdi mdi-bell-check icon'
            }
          ]
        };
        break;
      }
      case 'analytics-reports': {
        result = {
          id: entity.name,
          name: entity.name,
          createdBy: entity.createdBy,
          date: entity.date,
          schedule: entity.schedule,
          destinations: entity.destinations,
          icon: { icon: 'delete', color: '', class: 'delete-icon' }
        };
        break;
      }
      case 'sla-profiles': {
        result = {
          id: entity.id,
          name: entity.name,
          description: entity.description,
          nearFar: TableDataSourceService.getIconInfo(entity.nearFar, 'near-far'),
          farNear: TableDataSourceService.getIconInfo(entity.farNear, 'far-near'),
          roundTrip: TableDataSourceService.getIconInfo(entity.roundTrip, 'round-trip'),
          flows: entity.flows,
          shortterm: TableDataSourceService.getIconInfo(entity.shortTerm),
          longterm: TableDataSourceService.getIconInfo(entity.longTerm),
          disableAction: !this.isUserAuthorized('MANAGE_PERMISSIONS'),
          actionMenu: [
            { option: 'Delete', class: 'mdi mdi-delete icon' },
            { option: 'Clone', class: 'mdi  mdi-content-duplicate icon' }
          ]
        };
        break;
      }
      case 'run_report_view': {
        result = {
          Flow_ID: 126,
          SLA: 230,
          availability: 'Yes',
          UAS: 230,
          FLR: 30,
          OOS: 230,
          duplicate: 230,
          ifdv_avg: entity.IFDV_2way_Avg,
          ifdv_max: entity.IFDV_2way_Max,
          fd_avg: entity.FD_2way_Avg,
          fd_max: entity.FD_2way_Max,
          circuit_name: 'Circuit name',
          customer: 'Customer',
          sla_profile: 'Profile'
        };
        break;
      }
    }
    return result;
  }
  //eslint-disable-next-line
  getReportDate(dt: any, isUtc: boolean) {
    return isUtc
      ? dt.toLocaleDateString('default', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          timeZone: 'UTC'
        }) +
          ' ' +
          dt.toLocaleTimeString('default', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
            timeZone: 'UTC'
          })
      : dt.toLocaleDateString('default', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }) +
          ' ' +
          new Date(dt).toTimeString().replace(/.*(\d{2}:\d{2}:\d{2}).*/, '$1');
  }

  // eslint-disable-next-line
  getClusters$(payLoad): Observable<any> {
    const configurl = this.mapConfigPath;
    // eslint-disable-next-line
    let response: Observable<any>;
    try {
      response = this.apiService.post$<any>('/uimock', payLoad); // eslint-disable-line
    } catch {
      // eslint-disable-next-line
      console.info('No custom map configuration was available from the server.');
    }
    return response;
  }
  roundOff(value: number, decimals: number) {
    const shift = Math.pow(10, decimals);
    return Math.round(value * shift + 0.00000000000001) / shift;
  }
}
